import { ICellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/iCellRenderer';
import { AnswerView } from '../interfaces/CommunicationHistory.type';
import {
  FIELD_CALL_INTERACTIONS,
  getLastMonthDate,
  getTodayDate,
  InteractionStatuses
} from './communicationHistory.constant';
import {
  TERMINAL_INTERACTION_SORT_ORDER,
  VALIDATION_MESSAGE
} from '@cp/pages/CaseDetails/constants/index';
import { getPinCodeDetails } from '../actions/addressesActions';
import { utcToIST, incrementDateByMins } from '@cp/src/utils/DateHelper';
import { createQueryParams } from '@cp/src/utils/QueryParamsHelper';
import dayjs from 'dayjs';
import { DateFormat } from '@cp/src/utils/DateHelper';
import {
  ADD_TIME,
  defaultPreviousLoanInteractionsFromDateString
} from '../components/CommunicationHistory/constants';
import {
  FROM_TIME,
  PREVIOUS_LOAN_ACCOUNT_INTERACTIONS,
  TO_TIME
} from '../components/PreviousLoanCommunicationHistory/constants';
import {
  IBuildUpdatedUrlParams,
  ICreateFieldCallInteractionsPayload
} from '../components/CommunicationHistory/type';

export const feedbackHavingInteractionStatus = (params: ICellRendererParams) => {
  const interactionStatusAnswers = params.data?.answerViews?.filter((answer: AnswerView) =>
    [
      InteractionStatuses.CUSTOMER_INTERACTION_STATUS,
      InteractionStatuses.HUMAN_REMINDER_FEEDBACK_FORM
    ].includes(answer.questionTag as InteractionStatuses)
  );

  if (interactionStatusAnswers?.length) return interactionStatusAnswers[0];

  const terminalInteractionStatusAnswers = params.data?.answerViews?.filter(
    (answer: AnswerView) =>
      (answer.questionTag as InteractionStatuses) ===
      InteractionStatuses.TERMINAL_INTERACTION_STATUS
  );

  if (terminalInteractionStatusAnswers?.length) {
    return (
      terminalInteractionStatusAnswers?.reduce((minRecord: AnswerView, answer: AnswerView) => {
        const { metadata } = answer;
        if (
          metadata &&
          typeof metadata === 'object' &&
          TERMINAL_INTERACTION_SORT_ORDER in metadata
        ) {
          const terminalSortOrder = metadata?.TERMINAL_INTERACTION_SORT_ORDER as number;
          if (
            minRecord === undefined ||
            terminalSortOrder < (minRecord?.metadata?.TERMINAL_INTERACTION_SORT_ORDER as number)
          ) {
            return answer;
          }
        }
        return minRecord;
      }, undefined) || terminalInteractionStatusAnswers[0]
    );
  }

  return undefined;
};
export const getValidPincodeLength = 6;
export const minPincodeValue = 100000;

export const checkValidPinCode = (pinCode: string) => {
  return (
    pinCode?.length === getValidPincodeLength &&
    !isNaN(Number(pinCode)) &&
    Number(pinCode) >= minPincodeValue
  );
};

export const validatePincode = async (value: string) => {
  if (checkValidPinCode(value)) {
    const isValidPincode = await getPinCodeDetails(value);
    if (isValidPincode) return true;
  }
  return VALIDATION_MESSAGE.PINCODE_VALIDATION_MESSAGE;
};

export const getDefaultDates = () => {
  const maxInputDate = incrementDateByMins(utcToIST(getTodayDate()), 1).toISOString().slice(0, 16);
  const defaultLastMonthDate = utcToIST(getLastMonthDate()).toISOString().slice(0, 16);
  const defaultPrevLoanFrom = dayjs(
    defaultPreviousLoanInteractionsFromDateString,
    DateFormat.ISO_DATE_FORMAT
  ).format(DateFormat.COMMUNICATION_HISTORY_DATE_FORMAT);

  return { maxInputDate, defaultLastMonthDate, defaultPrevLoanFrom };
};

export const createPayloadForFieldCallInteractions = ({
  isPreviousLoanInteraction = false,
  queryParams,
  previousLoanAccountParams,
  caseReferenceId,
  defaultLastMonthDate,
  maxInputDate,
  defaultPrevLoanFrom
}: ICreateFieldCallInteractionsPayload) => {
  return {
    pageNo: (queryParams?.pageIndex as number) || 0,
    pageSize: (queryParams?.pageSize as number) || 20,
    caseReferenceId: isPreviousLoanInteraction
      ? previousLoanAccountParams?.previousCaseReferenceId
      : caseReferenceId,
    from: isPreviousLoanInteraction
      ? defaultPrevLoanFrom
      : (queryParams?.[FROM_TIME] as string) || defaultLastMonthDate,
    to: (queryParams?.[TO_TIME] as string) || maxInputDate
  };
};

export const buildUpdatedUrl = ({
  queryParams,
  previousLoanAccountParams,
  rest,
  params
}: IBuildUpdatedUrlParams) =>
  createQueryParams({
    [FIELD_CALL_INTERACTIONS]: { ...queryParams, ...params },
    [PREVIOUS_LOAN_ACCOUNT_INTERACTIONS]: {
      ...previousLoanAccountParams
    },
    ...rest
  });

export const getDisbursementDate = (startDate: string) => startDate + ADD_TIME;
